import React, { FC, ReactElement, useEffect } from 'react';
import styled from 'styled-components';
import { forDesktop, forIE, forTablet } from '@westwing/ui-kit/breakPoints';
import { useDispatch, useSelector } from 'react-redux';
import StateInterface from '../../../redux/types';
import usePrevious from '../../../hooks/usePrevious';
import ScrollableArea from '../ScrollableArea';
import ErrorBoundary from '../../Generic/ErrorBoundaries';
import GenericErrorRetry from '../../Generic/ErrorPages/GenericErrorRetry';
import Header from './Header';
import { getOverlayById } from 'Client/redux/overlays/selectors';
import { hideOverlay } from 'Client/redux/overlays/actions';
import { OverlaysState } from 'Client/redux/overlays/types';

const StyledWrapper = styled.div`
    display: flex;
    align-items: center;
    position: fixed;
    width: 100%;
    height: 100%;
    left: 0;
    top: 0;
    overflow: hidden;
    max-height: 100%;
    z-index: 100;
`;

const StyledContent = styled.div<{ isFullWidth: boolean; position?: 'bottom' | 'right' }>`
    position: relative;
    bottom: auto;
    max-height: 100vh;
    overflow: auto;
    top: 0;
    display: block;
    left: 0;
    width: 100%;
    text-align: center;
    transition: 0.2s ease-in-out;
    overflow-y: auto;
    -ms-overflow-style: none; /* IE 10+ */
    scrollbar-width: none; /* Firefox */
    z-index: ${({ theme }) => theme.zIndexPopUpOverlay};
    ${({ isFullWidth }) => (isFullWidth ? '' : 'margin: 0 8%;')}

    &::-webkit-scrollbar {
        display: none; /* Safari and Chrome */
    }
    ${forDesktop`
        width: 315px;
        margin: 0 auto;
    `}
`;

const StyledBackdrop = styled.div`
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: ${({ theme }) => theme.colors.skeleton02};
`;

export const RightStyledContent = styled(StyledContent)`
    ${forDesktop`
        height: 100%;
        width: 375px;
        margin-right: 0;
        max-height: 100vh;
        > div {
            max-height: 100vh;
        }
    `}
`;

export const BottomStyledContent = styled(StyledContent)`
    width: 100%;
    margin: 0;
    margin-top: auto;
    max-height: 85vh;
    > div {
        display: flex;
        max-height: 85vh;
        overflow: hidden;
    }

    ${forTablet`
        width: 100%;
        margin: 0 8%;
        max-height: 95vh;
        > div {
            max-height: 95vh;
        }
    `}

    ${forDesktop`
        width: 375px;
        margin: 0px auto;
        max-height: 95vh;
        > div {
            max-height: 95vh;
        }
    `}

    ${forIE`
        max-height: 95vh;
        height: 95vh;
        > div {
            height: 95vh;
            max-height: 95vh;
        }
    `}
`;

export interface BaseOverlayProps {
    children: React.ReactElement;
    identifier: string;
    hasUndismissibleBackdrop?: boolean;
    dismissFunction?: (origin?: 'closeBtn' | 'overlay') => void;
    isFullWidth?: boolean;
    hasHeader?: boolean;
    showOverlay?: Function;
    callback?: Function;
    className?: string;
    position?: 'bottom' | 'right';
    hasDismissButton?: boolean;
}

const BaseOverlay: FC<BaseOverlayProps> = ({
    children = null,
    identifier,
    hasHeader = true,
    hasUndismissibleBackdrop = false,
    isFullWidth = true,
    dismissFunction,
    callback = () => {},
    className = '',
    position,
    hasDismissButton = true,
}): ReactElement => {
    const dispatch = useDispatch();

    const handleDismissClick =
        dismissFunction ||
        ((): void => {
            dispatch(hideOverlay(identifier));
        });

    const overlays = useSelector<StateInterface, OverlaysState>(state => state.overlays);
    const isActive = Boolean(getOverlayById(overlays, identifier)?.isActive);

    const didOverlayClose = usePrevious(isActive) && !isActive;

    useEffect(() => {
        callback();
    }, [callback, didOverlayClose]);

    const id = identifier.toLocaleLowerCase();

    let ContentElement = StyledContent;
    if (position === 'right') {
        ContentElement = RightStyledContent;
    }

    if (position === 'bottom') {
        ContentElement = BottomStyledContent;
    }

    return (
        <ErrorBoundary boundaryName={`Popup-${id}`} fallbackComponent={<GenericErrorRetry />}>
            {isActive && (
                <StyledWrapper data-testid={id}>
                    <ContentElement isFullWidth={isFullWidth} className={className}>
                        <ScrollableArea>
                            {hasHeader && (
                                <Header
                                    handleDismissClick={() => handleDismissClick('closeBtn')}
                                    identifier={identifier}
                                    hasDismissButton={hasDismissButton}
                                />
                            )}
                            {children}
                        </ScrollableArea>
                    </ContentElement>
                    <StyledBackdrop
                        onClick={!hasUndismissibleBackdrop ? () => handleDismissClick('overlay') : () => {}}
                        data-identifier={identifier}
                        data-testid="popup-backdrop"
                    />
                </StyledWrapper>
            )}
        </ErrorBoundary>
    );
};

export default BaseOverlay;
